import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClerkAuth } from '../contexts/ClerkAuthContext';
import { useTranslation } from 'react-i18next';
import { Sparkles, User, Globe, ChevronDown } from 'lucide-react';

const LazyUserButton = lazy(() =>
  import('@clerk/clerk-react').then(mod => ({ default: mod.UserButton }))
);

const UserButtonFallback = () => (
  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#E4DDC9' }} />
);

// Brand tokens — kept in sync with the hero
const COLORS = {
  cream: '#FBF7EE',
  creamDeep: '#F4EEDF',
  border: '#E4DDC9',
  ink: '#16161D',
  inkSoft: '#3A3A45',
  brand: '#6D28D9',
};

const FONT_DISPLAY = '"Instrument Serif", Georgia, serif';
const FONT_BODY = '"DM Sans", system-ui, -apple-system, sans-serif';

// Inline brand-styled language switcher — matches header palette
const BrandLanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const languages = [
    { code: 'fr', label: 'Français', short: 'FR' },
    { code: 'en', label: 'English', short: 'EN' },
  ];
  const current = languages.find(l => l.code === i18n.language) || languages[0];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const change = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('synkro_language', code);
    setIsOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Changer de langue"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          background: 'transparent',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '999px',
          cursor: 'pointer',
          color: COLORS.inkSoft,
          fontFamily: FONT_BODY,
          fontSize: '14px',
          fontWeight: 500,
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <Globe size={14} color={COLORS.inkSoft} aria-hidden="true" />
        <span>{compact ? current.short : current.label}</span>
        <ChevronDown
          size={14}
          color={COLORS.inkSoft}
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            margin: 0,
            padding: '6px',
            listStyle: 'none',
            background: COLORS.cream,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '14px',
            boxShadow: '0 12px 28px rgba(22, 22, 29, 0.10)',
            zIndex: 1100,
            minWidth: '160px',
          }}
        >
          {languages.map((lang) => {
            const selected = lang.code === i18n.language;
            return (
              <li key={lang.code} role="option" aria-selected={selected}>
                <button
                  onClick={() => change(lang.code)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    border: 'none',
                    background: selected ? COLORS.creamDeep : 'transparent',
                    color: COLORS.ink,
                    fontFamily: FONT_BODY,
                    fontSize: '14px',
                    fontWeight: selected ? 600 : 500,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) e.currentTarget.style.background = COLORS.creamDeep;
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <span style={{ fontFamily: FONT_BODY, fontSize: '12px', fontWeight: 600, color: COLORS.inkSoft, minWidth: '22px' }}>
                    {lang.short}
                  </span>
                  <span>{lang.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, isLoaded } = useClerkAuth();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect scroll past 50px to add separation
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  // Close menu on route change
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  // Close menu on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setIsMenuOpen(false); };
    if (isMenuOpen) {
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
  }, [isMenuOpen]);

  const handleNavigate = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // ---- Reusable button styles ----

  const ghostButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 18px',
    background: 'transparent',
    border: `1.5px solid ${COLORS.ink}`,
    borderRadius: '999px',
    color: COLORS.ink,
    fontFamily: FONT_BODY,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s ease, color 0.2s ease',
  };

  const primaryButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 18px',
    background: COLORS.ink,
    border: `1.5px solid ${COLORS.ink}`,
    borderRadius: '999px',
    color: '#FFFFFF',
    fontFamily: FONT_BODY,
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: `0 4px 0 ${COLORS.brand}`,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  };

  const navLinkStyle = {
    background: 'transparent',
    border: 'none',
    color: COLORS.inkSoft,
    fontFamily: FONT_BODY,
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '8px 14px',
    borderRadius: '999px',
    transition: 'background 0.2s ease, color 0.2s ease',
  };

  // ---- Hamburger ----

  const HamburgerIcon = ({ isOpen, onClick, label }) => (
    <button
      onClick={onClick}
      aria-label={label}
      aria-haspopup="true"
      aria-expanded={isOpen}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '44px',
        height: '44px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '10px',
        zIndex: 1002,
      }}
    >
      <span style={{
        display: 'block',
        width: '22px',
        height: '2px',
        backgroundColor: COLORS.ink,
        borderRadius: '2px',
        transition: 'all 0.3s ease',
        transform: isOpen ? 'rotate(45deg) translateY(6px)' : 'rotate(0)',
        marginBottom: isOpen ? '0' : '5px',
      }} />
      <span style={{
        display: 'block',
        width: '22px',
        height: '2px',
        backgroundColor: COLORS.ink,
        borderRadius: '2px',
        transition: 'all 0.3s ease',
        opacity: isOpen ? 0 : 1,
        marginBottom: isOpen ? '0' : '5px',
      }} />
      <span style={{
        display: 'block',
        width: '22px',
        height: '2px',
        backgroundColor: COLORS.ink,
        borderRadius: '2px',
        transition: 'all 0.3s ease',
        transform: isOpen ? 'rotate(-45deg) translateY(-6px)' : 'rotate(0)',
      }} />
    </button>
  );

  // ---- Mobile drawer ----

  const MobileDrawer = () => (
    <>
      <div
        onClick={() => setIsMenuOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(22, 22, 29, 0.45)',
          zIndex: 1000,
          opacity: isMenuOpen ? 1 : 0,
          visibility: isMenuOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
        }}
        aria-hidden="true"
      />

      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(300px, 80vw)',
          backgroundColor: COLORS.cream,
          boxShadow: '-12px 0 30px rgba(22, 22, 29, 0.15)',
          zIndex: 1001,
          transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navigation"
      >
        {/* Drawer header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/logo-synkro.svg" alt="Synkro" style={{ height: '28px', display: 'block' }} />
          </div>
          <HamburgerIcon
            isOpen={isMenuOpen}
            onClick={() => setIsMenuOpen(false)}
            label="Fermer le menu"
          />
        </div>

        {/* Nav links */}
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          gap: '4px',
          flex: 1,
        }}>
          <button
            onClick={() => handleNavigate('/')}
            style={{
              ...navLinkStyle,
              fontSize: '17px',
              padding: '14px 16px',
              textAlign: 'left',
              borderRadius: '12px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {t('nav.home')}
          </button>

          <button
            onClick={() => handleNavigate('/pricing')}
            style={{
              ...navLinkStyle,
              fontSize: '17px',
              padding: '14px 16px',
              textAlign: 'left',
              borderRadius: '12px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {t('nav.pricing')}
          </button>

          {isLoaded && isSignedIn && (
            <button
              onClick={() => handleNavigate('/dashboard')}
              style={{
                ...navLinkStyle,
                fontSize: '17px',
                padding: '14px 16px',
                textAlign: 'left',
                borderRadius: '12px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {t('nav.dashboard')}
            </button>
          )}

          <div style={{ height: '1px', backgroundColor: COLORS.border, margin: '12px 0' }} />

          <div style={{ padding: '8px 4px' }}>
            <BrandLanguageSwitcher />
          </div>
        </nav>

        {/* Auth section */}
        <div style={{
          padding: '20px',
          borderTop: `1px solid ${COLORS.border}`,
          backgroundColor: COLORS.creamDeep,
        }}>
          {isLoaded && isSignedIn ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              justifyContent: 'center',
            }}>
              <Suspense fallback={<UserButtonFallback />}>
                <LazyUserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: { width: '44px', height: '44px' } } }}
                />
              </Suspense>
              <span style={{
                fontFamily: FONT_BODY,
                fontSize: '14px',
                color: COLORS.inkSoft,
              }}>
                {t('nav.dashboard')}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => handleNavigate('/sign-in')}
                style={{ ...ghostButtonStyle, width: '100%', padding: '12px 18px' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = COLORS.ink;
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = COLORS.ink;
                }}
              >
                {t('nav.login')}
              </button>

              <button
                onClick={() => handleNavigate('/sign-up')}
                style={{ ...primaryButtonStyle, width: '100%', padding: '12px 18px' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 0 ${COLORS.brand}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 0 ${COLORS.brand}`;
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(2px)';
                  e.currentTarget.style.boxShadow = `0 2px 0 ${COLORS.brand}`;
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 0 ${COLORS.brand}`;
                }}
              >
                {t('nav.signup')}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // ---- Render ----

  return (
    <>
      <nav
        aria-label="Menu principal"
        style={{
          position: 'sticky',
          top: 0,
          background: COLORS.cream,
          padding: isMobile ? '12px 16px' : '14px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: isScrolled ? `1px solid ${COLORS.border}` : '1px solid transparent',
          boxShadow: isScrolled ? '0 6px 20px rgba(22, 22, 29, 0.06)' : 'none',
          transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
          zIndex: 1000,
        }}
      >
        {/* Logo */}
        <button
          onClick={() => navigate('/')}
          aria-label="Synkro — Accueil"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'transparent',
            border: 'none',
            padding: '4px 6px',
            cursor: 'pointer',
            borderRadius: '8px',
          }}
        >
          <img src="/logo-synkro.svg" alt="Synkro" style={{ height: isMobile ? '32px' : '36px', display: 'block' }} />
        </button>

        {/* Desktop nav links */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => navigate('/')}
              style={navLinkStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; e.currentTarget.style.color = COLORS.ink; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = COLORS.inkSoft; }}
            >
              {t('nav.home')}
            </button>

            <button
              onClick={() => navigate('/pricing')}
              style={navLinkStyle}
              onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; e.currentTarget.style.color = COLORS.ink; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = COLORS.inkSoft; }}
            >
              {t('nav.pricing')}
            </button>

            {isLoaded && isSignedIn && (
              <button
                onClick={() => navigate('/dashboard')}
                style={navLinkStyle}
                onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.creamDeep; e.currentTarget.style.color = COLORS.ink; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = COLORS.inkSoft; }}
              >
                {t('nav.dashboard')}
              </button>
            )}
          </div>
        )}

        {/* Desktop right cluster */}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <BrandLanguageSwitcher compact />

            {isLoaded && isSignedIn ? (
              <Suspense fallback={<UserButtonFallback />}>
                <LazyUserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: { width: '40px', height: '40px' } } }}
                />
              </Suspense>
            ) : (
              <>
                <button
                  onClick={() => navigate('/sign-in')}
                  style={ghostButtonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = COLORS.ink;
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = COLORS.ink;
                  }}
                >
                  {t('nav.login')}
                </button>

                <button
                  onClick={() => navigate('/sign-up')}
                  style={primaryButtonStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 0 ${COLORS.brand}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 0 ${COLORS.brand}`;
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(2px)';
                    e.currentTarget.style.boxShadow = `0 2px 0 ${COLORS.brand}`;
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 0 ${COLORS.brand}`;
                  }}
                >
                  {t('nav.signup')}
                </button>
              </>
            )}
          </div>
        )}

        {/* Mobile cluster: signup CTA + login icon + hamburger */}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLoaded && isSignedIn ? (
              <Suspense fallback={<UserButtonFallback />}>
                <LazyUserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: { width: '36px', height: '36px' } } }}
                />
              </Suspense>
            ) : (
              <>
                <button
                  onClick={() => navigate('/sign-in')}
                  aria-label={t('nav.login')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    background: 'transparent',
                    border: `1.5px solid ${COLORS.ink}`,
                    borderRadius: '999px',
                    color: COLORS.ink,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease, color 0.2s ease',
                  }}
                >
                  <User size={18} />
                </button>

                <button
                  onClick={() => navigate('/sign-up')}
                  style={{
                    ...primaryButtonStyle,
                    padding: '9px 14px',
                    fontSize: '13px',
                  }}
                >
                  {t('nav.signup')}
                </button>
              </>
            )}
            <HamburgerIcon
              isOpen={isMenuOpen}
              onClick={() => setIsMenuOpen(true)}
              label="Ouvrir le menu"
            />
          </div>
        )}
      </nav>

      {isMobile && <MobileDrawer />}
    </>
  );
};

export default Header;
